import { useState, useEffect } from 'react';
import { CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { getDailyReport, getTOSControlsState } from '../../../services/firestore/opsService';
import type { DailyHealthReport, TOSControlsState } from '../../../types/ops';

const C = {
  text: '#18181B', textSec: '#52525B', textMuted: '#A1A1AA',
  rose: '#E11D48', pass: '#059669', warn: '#B45309', fail: '#E11D48',
  bgCard: '#FFFFFF', border: '#E4E4E7',
  passBg: '#D1FAE5', warnBg: '#FEF3C7', failBg: '#FFF1F2',
};

const SUBTABS = ['TOS Controls', 'Auth & Access', 'Secret Health', 'Compliance', 'Event Bus'] as const;
type SubTab = typeof SUBTABS[number];

export function SecurityTab() {
  const [subTab, setSubTab] = useState<SubTab>('TOS Controls');
  const [report, setReport] = useState<DailyHealthReport | null>(null);
  const [tosState, setTosState] = useState<TOSControlsState | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const today = new Date().toISOString().slice(0, 10);
        const [r, t] = await Promise.all([getDailyReport(today), getTOSControlsState()]);
        setReport(r);
        setTosState(t);
      } catch (err) {
        console.error('Failed to load security data:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: C.textMuted }}>Loading security data...</div>;

  return (
    <div>
      {/* Sub-tab bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        {SUBTABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setSubTab(tab)}
            style={{
              padding: '6px 14px', fontSize: 13, borderRadius: 4,
              border: `1px solid ${C.border}`,
              background: subTab === tab ? C.rose : C.bgCard,
              color: subTab === tab ? '#fff' : C.textSec,
              cursor: 'pointer', fontWeight: subTab === tab ? 600 : 400,
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Sub-tab content */}
      {subTab === 'TOS Controls' && <TOSControlsSubTab report={report} tosState={tosState} />}
      {subTab === 'Auth & Access' && <AuthAccessSubTab report={report} />}
      {subTab === 'Secret Health' && <SecretHealthSubTab />}
      {subTab === 'Compliance' && <ComplianceSubTab report={report} />}
      {subTab === 'Event Bus' && <EventBusSubTab report={report} />}
    </div>
  );
}

// ── TOS Controls ──

function TOSControlsSubTab({ report }: { report: DailyHealthReport | null; tosState: TOSControlsState | null }) {
  const pk = report?.promiseKeeper;

  return (
    <div>
      {/* PK summary bar */}
      {pk && (
        <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 20, marginBottom: 16 }}>
          <p style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, color: C.textMuted, marginBottom: 12 }}>
            Promise Keeper Status
          </p>
          <div style={{ display: 'flex', gap: 24 }}>
            <Stat label="Monitored" value={`${pk.monitored}/${pk.monitored + pk.unmonitored}`} color={C.text} />
            <Stat label="Healthy" value={pk.healthy} color={C.pass} />
            <Stat label="Degraded" value={pk.degraded} color={C.warn} />
            <Stat label="Failing" value={pk.failing} color={C.fail} />
            <Stat label="Unmonitored" value={pk.unmonitored} color={C.textMuted} />
          </div>
        </div>
      )}

      {/* TOS compliance checks */}
      <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 20 }}>
        <p style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, color: C.textMuted, marginBottom: 12 }}>
          8 TOS Compliance Checks (Daily)
        </p>
        {!report ? (
          <p style={{ fontSize: 14, color: C.textSec }}>No check data yet.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <Th>Check</Th>
                <Th>Status</Th>
                <Th>Detail</Th>
              </tr>
            </thead>
            <tbody>
              {[
                { name: 'Consent Service', check: report.checks.consentService },
                { name: 'Deletion Pipeline', check: report.checks.deletionPipeline },
                { name: 'Audit Logging', check: report.checks.auditLogging },
                { name: 'Breach Detection', check: report.checks.breachDetection },
                { name: 'PHI Access Controls', check: report.checks.phiAccessControls },
                { name: 'Vendor BAA Currency', check: report.checks.vendorBaaCurrency },
                { name: 'Data Rights Pipeline', check: report.checks.dataRightsPipeline },
                { name: 'Entity Consistency', check: report.checks.entityConsistency },
              ].map(({ name, check }) => (
                <tr key={name} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: '10px 12px', fontSize: 14, fontWeight: 500 }}>{name}</td>
                  <td style={{ padding: '10px 12px' }}><StatusBadge status={check.status} /></td>
                  <td style={{ padding: '10px 12px', fontSize: 13, color: C.textSec }}>{check.message}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ── Auth & Access ──

function AuthAccessSubTab({ report }: { report: DailyHealthReport | null }) {
  const phi = report?.checks.phiAccessControls;
  const checks = [
    { name: 'Firestore Rules Secure', status: phi ? (phi.rulesSecure ? 'healthy' : 'critical') : 'not_checked', note: phi?.message },
    { name: 'MFA Enforced (Admin)', status: phi ? (phi.mfaEnforced ? 'healthy' : 'degraded') : 'not_checked', note: 'Requires Phase 6 auth' },
    { name: 'Encryption at Rest', status: phi ? (phi.encryptionActive ? 'healthy' : 'critical') : 'not_checked', note: 'Firebase default AES-256' },
    { name: 'Server-Only _ops/ Writes', status: 'not_checked', note: 'Enforced via Firestore rules — requires Phase 6' },
    { name: 'Admin Role Gates', status: 'not_checked', note: 'Requires Phase 6 RBAC implementation' },
    { name: 'Vendor JWT Scoping', status: 'not_checked', note: 'Requires Phase 6E vendor auth' },
  ];

  return (
    <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 20 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><Th>Check</Th><Th>Status</Th><Th>Note</Th></tr></thead>
        <tbody>
          {checks.map((c) => (
            <tr key={c.name} style={{ borderBottom: `1px solid ${C.border}` }}>
              <td style={{ padding: '10px 12px', fontSize: 14 }}>{c.name}</td>
              <td style={{ padding: '10px 12px' }}><StatusBadge status={c.status} /></td>
              <td style={{ padding: '10px 12px', fontSize: 13, color: C.textSec }}>{c.note || ''}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Secret Health ──

function SecretHealthSubTab() {
  const secrets = [
    { name: 'ANTHROPIC_API_KEY', store: 'Firebase Secret Manager' },
    { name: 'STRIPE_SECRET_KEY', store: 'Firebase Secret Manager' },
    { name: 'SLACK_OPS_WEBHOOK_URL', store: 'Firebase Secret Manager' },
    { name: 'GEMINI_API_KEY', store: 'Firebase Secret Manager' },
  ];

  return (
    <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 20 }}>
      <div style={{
        background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 4,
        padding: '12px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <AlertTriangle size={14} style={{ color: C.warn, flexShrink: 0 }} />
        <span style={{ fontSize: 13, color: '#92400E' }}>
          Secret rotation monitoring activates with Phase 6 auth. Secrets are currently managed manually in Firebase Secret Manager.
        </span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><Th>Secret</Th><Th>Storage</Th><Th>Rotation</Th></tr></thead>
        <tbody>
          {secrets.map((s) => (
            <tr key={s.name} style={{ borderBottom: `1px solid ${C.border}` }}>
              <td style={{ padding: '10px 12px', fontSize: 13, fontFamily: "'JetBrains Mono', monospace" }}>{s.name}</td>
              <td style={{ padding: '10px 12px', fontSize: 13, color: C.textSec }}>{s.store}</td>
              <td style={{ padding: '10px 12px' }}><StatusBadge status="not_checked" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Compliance ──

function ComplianceSubTab({ report }: { report: DailyHealthReport | null }) {
  const complianceChecks = [
    { name: 'HIPAA BAA Active', mode: 'auto' as const, status: report ? 'healthy' : 'not_checked' },
    { name: 'SOC 2 Controls', mode: 'manual' as const, status: 'manual' },
    { name: 'Consent Collection (7 purposes)', mode: 'auto' as const, status: report ? report.checks.consentService.status : 'not_checked' },
    { name: 'Data Deletion SLA (30 days)', mode: 'auto' as const, status: report ? report.checks.deletionPipeline.status : 'not_checked' },
    { name: 'Audit Trail Complete', mode: 'auto' as const, status: report ? report.checks.auditLogging.status : 'not_checked' },
    { name: 'Breach Notification Pipeline', mode: 'auto' as const, status: report ? report.checks.breachDetection.status : 'not_checked' },
    { name: 'Vendor BAA Currency', mode: 'auto' as const, status: report ? report.checks.vendorBaaCurrency.status : 'not_checked' },
    { name: 'PHI Minimum Necessary', mode: 'manual' as const, status: 'manual' },
    { name: 'Employee Training', mode: 'manual' as const, status: 'manual' },
    { name: 'Incident Response Plan', mode: 'auto' as const, status: report ? 'healthy' : 'not_checked' },
    { name: 'Security Headers', mode: 'auto' as const, status: report?.checks.securityHeaders ? report.checks.securityHeaders.status : 'not_checked' },
  ];

  return (
    <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 20 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><Th>Check</Th><Th>Mode</Th><Th>Status</Th></tr></thead>
        <tbody>
          {complianceChecks.map((c) => (
            <tr key={c.name} style={{ borderBottom: `1px solid ${C.border}` }}>
              <td style={{ padding: '10px 12px', fontSize: 14 }}>{c.name}</td>
              <td style={{ padding: '10px 12px' }}>
                <span style={{
                  fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                  color: c.mode === 'auto' ? C.pass : C.textMuted,
                }}>
                  {c.mode}
                </span>
              </td>
              <td style={{ padding: '10px 12px' }}><StatusBadge status={c.status} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Event Bus ──

function EventBusSubTab({ report }: { report: DailyHealthReport | null }) {
  const eb = report?.checks.eventBus;

  return (
    <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 20 }}>
      {!eb ? (
        <p style={{ fontSize: 14, color: C.textSec }}>No Event Bus data yet.</p>
      ) : (
        <div>
          <div style={{ display: 'flex', gap: 24, marginBottom: 16 }}>
            <Stat label="Pending >24h" value={eb.pendingOver24h} color={eb.pendingOver24h > 0 ? C.warn : C.pass} />
            <Stat label="Failed (24h)" value={eb.failedIn24h} color={eb.failedIn24h > 0 ? C.rose : C.pass} />
            <Stat label="Status" value={eb.status.toUpperCase()} color={eb.status === 'healthy' ? C.pass : C.warn} />
          </div>
          <p style={{ fontSize: 13, color: C.textSec }}>{eb.message}</p>
        </div>
      )}
    </div>
  );
}

// ── Shared ──

function StatusBadge({ status }: { status: string }) {
  const cfg = status === 'healthy'
    ? { icon: CheckCircle2, color: C.pass, bg: C.passBg, label: 'PASS' }
    : status === 'degraded'
    ? { icon: AlertTriangle, color: C.warn, bg: C.warnBg, label: 'WARN' }
    : status === 'not_checked'
    ? { icon: AlertTriangle, color: C.textMuted, bg: '#F4F4F5', label: 'NOT CHECKED' }
    : status === 'manual'
    ? { icon: CheckCircle2, color: '#6366F1', bg: '#EEF2FF', label: 'MANUAL' }
    : { icon: XCircle, color: C.fail, bg: C.failBg, label: 'FAIL' };
  const Icon = cfg.icon;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      fontSize: 12, fontWeight: 600, color: cfg.color, background: cfg.bg,
      padding: '2px 8px', borderRadius: 4,
    }}>
      <Icon size={12} /> {cfg.label}
    </span>
  );
}

function Stat({ label, value, color }: { label: string; value: number | string; color: string }) {
  return (
    <div>
      <p style={{ fontSize: 20, fontWeight: 700, color }}>{value}</p>
      <p style={{ fontSize: 12, color: C.textSec }}>{label}</p>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th style={{
      textAlign: 'left', fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
      letterSpacing: 0.5, color: '#A1A1AA', padding: '8px 12px', borderBottom: '1px solid #E4E4E7',
    }}>
      {children}
    </th>
  );
}
